"use client";

import * as React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/layouts/home/logo";

type Status = "idle" | "loading" | "success" | "error";

type ContactFormData = {
  name: string;
  email: string;
  company: string;
  message: string;
};

const INITIAL_FORM_DATA: ContactFormData = {
  name: "",
  email: "",
  company: "",
  message: "",
};

export function ContactSection() {
  const [formData, setFormData] =
    React.useState<ContactFormData>(INITIAL_FORM_DATA);
  const [status, setStatus] = React.useState<Status>("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");

    try {
      const res = await fetch("/api/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        setStatus("error");
        return;
      }

      setStatus("success");
      setFormData(INITIAL_FORM_DATA);
    } catch {
      setStatus("error");
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section className="relative overflow-hidden bg-black pt-16 pb-16 lg:pt-20 lg:pb-20">
      <div className="relative z-10 mx-auto w-full max-w-[1512px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-12 lg:gap-16 xl:grid-cols-2">
          {/* Left */}
          <div className="flex flex-col justify-start px-0 xl:pl-[60px]">
            <div className="mb-12 w-[180px]">
              <Logo />
            </div>

            <h2 className="mb-8 text-center font-display text-[clamp(28px,3.5vw,48px)] font-bold leading-[1.2] tracking-[-0.02em] text-white xl:text-left">
              Une question ? N&apos;hésitez pas,
              <br />
              Contactez nous !
            </h2>

            <a
              href="mailto:contact@stack-flow.io"
              className="mb-10 block text-center font-[Switzer,sans-serif] text-[18px] text-white/80 transition-colors hover:text-white xl:text-left"
            >
              contact@stack-flow.io
            </a>
          </div>

          {/* Right */}
          <div>
            <form
              onSubmit={handleSubmit}
              className="rounded-3xl border border-white/10 bg-linear-to-br from-white/5 to-white/2 p-8 backdrop-blur-xl sm:p-10 lg:p-12"
            >
              {/* Field 01 */}
              <div className="mb-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#6729FF]">
                    <span className="font-display text-[14px] font-semibold text-white">
                      01
                    </span>
                  </div>

                  <div className="flex-1">
                    <label
                      htmlFor="name"
                      className="mb-3 block font-display text-[18px] font-semibold text-white"
                    >
                      Quel est votre nom ?
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      type="text"
                      placeholder="Entrez votre nom complet"
                      className="rounded-none border-0 border-b border-white/20 bg-transparent px-0 font-[Switzer,sans-serif] text-[15px] text-white placeholder:text-white/40 focus-visible:border-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>
              </div>

              {/* Field 02 */}
              <div className="mb-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20">
                    <span className="font-display text-[14px] font-semibold text-white/60">
                      02
                    </span>
                  </div>

                  <div className="flex-1">
                    <label
                      htmlFor="email"
                      className="mb-3 block font-display text-[18px] font-semibold text-white"
                    >
                      Quelle est votre adresse email ?
                    </label>
                    <Input
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      type="email"
                      placeholder="exemple@gmail.com"
                      className="rounded-none border-0 border-b border-white/20 bg-transparent px-0 font-[Switzer,sans-serif] text-[15px] text-white placeholder:text-white/40 focus-visible:border-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>
              </div>

              {/* Field 03 */}
              <div className="mb-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20">
                    <span className="font-display text-[14px] font-semibold text-white/60">
                      03
                    </span>
                  </div>

                  <div className="flex-1">
                    <label
                      htmlFor="company"
                      className="mb-3 block font-display text-[18px] font-semibold text-white"
                    >
                      Quel service recherchez-vous ?
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      type="text"
                      placeholder="Automatisation, Monitoring, AI Chat..."
                      className="rounded-none border-0 border-b border-white/20 bg-transparent px-0 font-[Switzer,sans-serif] text-[15px] text-white placeholder:text-white/40 focus-visible:border-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>
              </div>

              {/* Field 04 */}
              <div className="mb-10">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20">
                    <span className="font-display text-[14px] font-semibold text-white/60">
                      04
                    </span>
                  </div>

                  <div className="flex-1">
                    <label
                      htmlFor="message"
                      className="mb-3 block font-display text-[18px] font-semibold text-white"
                    >
                      Votre message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Bonjour, pouvez-vous m'aider avec..."
                      rows={3}
                      className="w-full resize-none rounded-none border-0 border-b border-white/20 bg-transparent px-0 font-[Switzer,sans-serif] text-[15px] leading-[1.6] text-white placeholder:text-white/40 outline-none focus:border-white/40"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex flex-col items-center justify-center gap-4">
                <Button
                  type="submit"
                  disabled={status === "loading"}
                  className="rounded-full bg-[#6729FF] px-10 py-4 font-display text-[15px] font-semibold text-white transition-transform duration-300 ease-out hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 cursor-pointer"
                >
                  {status === "loading" ? "Envoi..." : "Envoyer mon message"}
                </Button>

                {status === "success" && (
                  <p className="font-medium text-green-400">
                    Message envoyé avec succès !
                  </p>
                )}
                {status === "error" && (
                  <p className="font-medium text-red-400">
                    Une erreur est survenue.
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
